import {getURISegment} from '@Utils/url.util';
import type {Component, ProjectData} from 'grapesjs';
import {GA4EventParamName} from '@Libraries/ga-events';
import {getGettyMediaCount} from './commands/editor';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src/index';
import {PALETTE_CUSTOM_STYLE} from '@/email-maker/grapesjs-email-editor-plugin/src/custom-style-types/auto-branding';
import type {SymbolStorageData} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';

import type {ColorPaletteInfoToStore} from '@Libraries/auto-branding-email-maker';

interface StorageData {
  templateContent: object;
  mode: string | null;
  idEmailTemplate: string | null;
  isInHouseDesigner: string | null;
  name: string;
  htmlContent: string;
  designIDs: string;
  mediaQueries: string;
  hasGifContent: boolean;
  hasPremContent: boolean;
  imageHashedFilenames: string;
  videoHashedFilenames: string;
  isScheduled: boolean;
  scheduledTime: string;
}

interface LoadResponse {
  templateContent: string;
}

interface SaveResponse {
  idEmailTemplate: string;
  idEmailCampaign: string;
}

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const storage = editor.StorageManager;

  const PMW_STORAGE = {
    onLoad: (data: Record<string, unknown>): Record<string, unknown> => {
      return data;
    },

    async load(): Promise<Record<string, unknown>> {
      const isNewMode: boolean = getURISegment(1) === window.PMW.emails.MODE_START;
      const idEmailTemplate: string | null = getURISegment(2);
      const canLoadTemplate: boolean = !isNewMode && !!idEmailTemplate;

      if (!canLoadTemplate) {
        return {};
      }

      try {
        const data = (await window.PMW.writeLocal('emailmaker/asyncLoadEmailTemplate', {
          idEmailTemplate,
        })) as LoadResponse;
        const jsonData = JSON.parse(data.templateContent) as Record<string, unknown>;

        window.PMW.idEmailTemplate = idEmailTemplate;

        if (!window.PMW.idEmailTemplate) {
          throw new Error('Template ID is not set after loading the template.');
        }

        updateDuplicateEmailHref(window.PMW.idEmailTemplate);
        editor.runCommand(window.PMW.emails.commands.UPDATE_SAVE_BUTTON, {state: window.PMW.emails.AUTOSAVE_STATE_SAVED});
        editor.trigger('storage:response', jsonData);
        return this.onLoad(jsonData);
      } catch {
        const errMsg = `could not load template for id ${idEmailTemplate}`;
        window.PMW.openErrorModal({message: window.i18next.t('pmwjs_could_not_load_template_for_id', {id: idEmailTemplate})});
        window.history.pushState(null, '', window.PMW.util.site_url('emailmaker/start/'));
        console.error(errMsg);
        throw new Error(errMsg);
      }
    },

    onStore: (data: Record<string, unknown>): StorageData => {
      let storageData;
      Object.keys(data).forEach((key: string) => {
        if (key === 'pages') {
          storageData = editor.runCommand(window.PMW.emails.commands.GET_STORAGE_DATA_WITHOUT_FOOTER, {data: data[key]}) as object;
        } else {
          storageData = data[key] as object;
        }

        if (key === 'symbols') {
          const symbolStaticData: SymbolStorageData = {};
          symbolStaticData.content = '[]';
          symbolStaticData.type = 'textnode';
          storageData = [symbolStaticData];
        }

        data[key] = JSON.stringify(storageData);
      });

      return {
        templateContent: data,
        mode: getURISegment(1),
        idEmailTemplate: getURISegment(2),
        isInHouseDesigner: getURISegment(3),
        name: editor.runCommand(window.PMW.emails.commands.GET_TEMPLATE_NAME) as string,
        htmlContent: editor.runCommand(window.PMW.emails.commands.GET_INLINED_HTML) as string,
        designIDs: editor.runCommand(window.PMW.emails.commands.GET_DESIGN_IDS) as string,
        mediaQueries: editor.runCommand(window.PMW.emails.commands.GET_MEDIA_QUERIES) as string,
        hasGifContent: editor.runCommand(window.PMW.emails.commands.DOES_EMAIL_CONTAIN_GIF) as boolean,
        hasPremContent: doesEmailContainPremiumContent(),
        imageHashedFilenames: editor.runCommand(window.PMW.emails.commands.GET_IMAGE_ASSET_HASHED_NAMES) as string,
        videoHashedFilenames: editor.runCommand(window.PMW.emails.commands.GET_VIDEO_ASSET_HASHED_NAMES) as string,
        isScheduled: window.PMW.emails.IS_SCHEDULED,
        scheduledTime: window.PMW.emails.SCHEDULED_TIME,
      };
    },

    async store(data: ProjectData, storageOptions: Record<string, unknown>): Promise<unknown> {
      editor.runCommand(window.PMW.emails.commands.BEFORE_SAVE);
      if (window.PMW.emails.IS_SAVING) {
        window.PMW.emails.SAVE_QUEUED = true;
        return {};
      }

      window.PMW.emails.IS_SAVING = true;

      try {
        const mergedData = {...data, ...storageOptions};
        const responseData = (await window.PMW.writeLocal('emailmaker/asyncSaveEmailTemplate', mergedData)) as SaveResponse;
        if (data.mode === window.PMW.emails.MODE_START || data.mode === window.PMW.emails.MODE_COPY) {
          if (data.mode === window.PMW.emails.MODE_START) {
            window.PMW.gtm.trackGA4CustomEvent('EmailMaker_DesignCreated', {[GA4EventParamName.SOURCE]: 'BlankCanvas'});
          } else if (data.mode === window.PMW.emails.MODE_COPY) {
            window.PMW.gtm.trackGA4CustomEvent('EmailMaker_DesignCreated', {[GA4EventParamName.SOURCE]: 'Template'});
          }

          window.PMW.idEmailTemplate = responseData.idEmailTemplate;
          updateDuplicateEmailHref(window.PMW.idEmailTemplate);
          updateIsOwnerAttribute('1');

          window.history.replaceState(null, '', window.PMW.util.site_url(`emailmaker/load/${window.PMW.idEmailTemplate}`));
        }

        if (!window.PMW.emails.HAS_SAVED) {
          window.PMW.gtm.trackGA4CustomEvent('EmailMaker_DesignSaved');
          window.PMW.emails.HAS_SAVED = true;
        }

        if (!window.PMW.idEmailCampaign && responseData.idEmailCampaign) {
          window.PMW.idEmailCampaign = responseData.idEmailCampaign;
          updateEmailCampaignIdAttribute(window.PMW.idEmailCampaign);
        }

        editor.runCommand(window.PMW.emails.commands.SET_AUTOSAVE, {autosave: true});
        editor.runCommand(window.PMW.emails.commands.AFTER_SAVE);
        window.PMW.emails.IS_SAVING = false;
        return data;
      } catch (e: unknown) {
        if (e && typeof e === 'object' && 'code' in e && 'message' in e && 'data' in e) {
          editor.runCommand(window.PMW.emails.commands.UNABLE_TO_SAVE, {
            code: e.code as number,
            message: e.message as string,
            data: e.data,
          });
        }
      }

      const colorPaletteData = extractColorPaletteData();
      if (shouldStoreColorPalette(colorPaletteData)) {
        await window.PMW.writeLocal('autobrandingemailmaker/asyncSaveUserColorPaletteAndUsage', {...colorPaletteData, idEmailTemplate: window.PMW.idEmailTemplate});
      }

      window.PMW.emails.IS_SAVING = false;

      return {};
    },
  };

  function extractColorPaletteData(): ColorPaletteInfoToStore | null {
    const mainWrapper = editor.runCommand(window.PMW.emails.commands.GET_MAIN_WRAPPER) as Component;
    const colorPaletteData = mainWrapper.getStyle(PALETTE_CUSTOM_STYLE) as unknown as string | null;

    if (!colorPaletteData) {
      return null;
    }

    const data = JSON.parse(colorPaletteData) as ColorPaletteInfoToStore;

    const dataToRetain: ColorPaletteInfoToStore = {colors: data.colors, name: data.name};
    mainWrapper.addStyle({[PALETTE_CUSTOM_STYLE]: JSON.stringify(dataToRetain)});

    return data;
  }

  function shouldStoreColorPalette(colorPaletteData: ColorPaletteInfoToStore | null): boolean {
    if (!colorPaletteData) {
      return false;
    }

    const {id, customizedFromId, customizedFromType} = colorPaletteData;
    return id !== undefined || (customizedFromId !== undefined && customizedFromType !== undefined);
  }

  function updateDuplicateEmailHref(id: string): void {
    // eslint-disable-next-line no-restricted-globals
    $('.js-duplicate-email-href').attr('href', window.PMW.util.site_url(`emailmaker/copy/${id}`));
  }

  function updateIsOwnerAttribute(value: string): void {
    // eslint-disable-next-line no-restricted-globals
    $('#html-editor-container').attr('data-do-set-autosave-active', value);
  }

  function updateEmailCampaignIdAttribute(value: string): void {
    // eslint-disable-next-line no-restricted-globals
    $('#html-editor-container').attr('data-id-campaign', value);
  }

  function doesEmailContainPremiumContent(): boolean {
    return getGettyMediaCount(editor, window.PMW.emails.mediaType.IMAGE) > 0 || getGettyMediaCount(editor, window.PMW.emails.mediaType.VIDEO) > 0;
  }

  storage.add('remote-storage', PMW_STORAGE);
};
